import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Tag } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { promptModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { api } from '@/lib/api/client'
import { crmKeys } from './api'
import { useCategories } from './categoriesApi'
import { toastError } from './ui'

export function CategoryBadge({ categoryId }: { categoryId: string }) {
  const categories = useCategories()
  const c = categories.data?.find((x) => x.id === categoryId)
  if (!c) return null
  return (
    <Badge tone={c.color}>
      <Tag className="size-3" aria-hidden />
      {c.name}
    </Badge>
  )
}

/** Müşterinin kategorisini değiştirir; isteğe bağlı karar notu aşama geçmişine yazılır. */
export function CategoryPicker({ customerId, categoryId, disabled }: { customerId: string; categoryId: string; disabled?: boolean }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const categories = useCategories()
  const assign = useMutation({
    mutationFn: (b: { category_id: string; note: string }) => api.put(`/api/v1/accounts/${customerId}/category`, b),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: crmKeys.customers }), qc.invalidateQueries({ queryKey: ['crm'] })]),
  })
  const options = [
    { value: 'none', label: t('crm.category.none') },
    ...(categories.data ?? []).filter((c) => c.isActive || c.id === categoryId).map((c) => ({ value: c.id, label: c.name })),
  ]
  const onChange = async (v: string) => {
    const next = v === 'none' ? '' : v
    if (next === categoryId) return
    const note = await promptModal({ title: t('crm.category.changeTitle'), description: t('crm.category.changeHint'), placeholder: t('crm.category.notePlaceholder'), confirmLabel: t('common.save') })
    if (note == null) return
    assign.mutate({ category_id: next, note: note.trim() }, { onSuccess: () => toast.success(t('crm.category.changed')), onError: toastError })
  }
  return <Select size="sm" value={categoryId || 'none'} onChange={(v) => void onChange(v)} options={options} disabled={disabled || assign.isPending} aria-label={t('crm.category.label')} />
}
